/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { personalDataStore } from 'stores/personalDataStore';
import { chatStore } from 'stores/chatStore';
import { chatApi, ChatConversation, ChatMessage } from 'api';
import { API_AUTH_URL } from 'api';
import SearchInput from 'components/SearchInput';
import { useSocketContext } from 'contexts/SocketContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDisplayName(user: { firstName?: string | null; lastName?: string | null; email?: string | null }) {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return full || user.email || 'Пользователь';
}

function getAvatarSrc(avatar?: string | null) {
  if (!avatar) return undefined;
  return `${API_AUTH_URL}${avatar}`;
}

/** Вернуть собеседника (не текущего пользователя) */
function getInterlocutor(conv: ChatConversation, myId: string) {
  return conv.participants.find((p) => p.userId !== myId)?.user ?? null;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const ConversationItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08)
  }
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOwn'
})<{ isOwn?: boolean }>(({ theme, isOwn }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  maxWidth: '70%',
  backgroundColor: isOwn
    ? alpha(theme.palette.primary.main, 0.12)
    : theme.palette.background.neutral,
  alignSelf: isOwn ? 'flex-end' : 'flex-start',
  whiteSpace: 'pre-line'
}));

const MessageInputBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0
}));

// ---------------------------------------------------------------------------
// Schedule Meeting Dialog
// ---------------------------------------------------------------------------

const AVAILABLE_DATES = ['Пн, 24 мар', 'Вт, 25 мар', 'Ср, 26 мар', 'Чт, 27 мар', 'Пт, 28 мар', 'Пн, 31 мар'];
const AVAILABLE_TIMES = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00'];

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (payload: { dates: string[]; times: string[]; durationHours: string; durationMinutes: string }) => void;
}

function ScheduleMeetingDialog({ open, onClose, onSend }: ScheduleDialogProps) {
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [durationH, setDurationH] = useState('1');
  const [durationM, setDurationM] = useState('0');

  const toggle = (arr: string[], val: string) =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  const handleSend = () => {
    onSend({ dates: selectedDates, times: selectedTimes, durationHours: durationH, durationMinutes: durationM });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Назначить встречу</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Предложите соискателю варианты даты и времени для проведения встречи
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Выберите дату</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {AVAILABLE_DATES.map((d) => (
            <Button key={d} size="small"
              variant={selectedDates.includes(d) ? 'contained' : 'outlined'}
              onClick={() => setSelectedDates(toggle(selectedDates, d))}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: 13 }}
            >{d}</Button>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Выберите время</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {AVAILABLE_TIMES.map((t) => (
            <Button key={t} size="small"
              variant={selectedTimes.includes(t) ? 'contained' : 'outlined'}
              onClick={() => setSelectedTimes(toggle(selectedTimes, t))}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: 13, minWidth: 72 }}
            >{t}</Button>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Выберите продолжительность</Typography>
        <Stack direction="row" spacing={1}>
          <Select value={durationH} onChange={(e) => setDurationH(e.target.value)} size="small" sx={{ flex: 1 }}>
            {['0', '1', '2', '3'].map((h) => <MenuItem key={h} value={h}>{h} ч</MenuItem>)}
          </Select>
          <Select value={durationM} onChange={(e) => setDurationM(e.target.value)} size="small" sx={{ flex: 1 }}>
            {['0', '15', '30', '45'].map((m) => <MenuItem key={m} value={m}>{m} мин</MenuItem>)}
          </Select>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Отмена</Button>
        <Button onClick={handleSend} variant="contained" disabled={!selectedDates.length || !selectedTimes.length}>
          Отправить приглашение
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Chat panel
// ---------------------------------------------------------------------------

interface ChatViewProps {
  conversation: ChatConversation;
  myId: string;
  onBack: () => void;
  onNewMessage: (msg: ChatMessage) => void;
}

function ChatView({ conversation, myId, onBack, onNewMessage }: ChatViewProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const interlocutor = getInterlocutor(conversation, myId);

  useEffect(() => {
    let active = true;
    setLoading(true);
    chatApi.getMessages(conversation.id)
      .then((msgs) => {
        if (active) {
          setMessages(msgs);
          // Помечаем как прочитанные при открытии диалога
          chatApi.markAsRead(conversation.id).catch(() => { });
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [conversation.id, onNewMessage]);

  const { socket } = useSocketContext();

  // WS: подписка на входящие сообщения этого диалога
  useEffect(() => {
    if (!myId || !socket) return;

    const handler = (msg: ChatMessage) => {
      if (msg.conversationId !== conversation.id) return;
      setMessages((prev) => [...prev, msg]);
      onNewMessage(msg);
      // Сразу помечаем как прочитанное, т.к. диалог открыт
      chatApi.markAsRead(conversation.id).catch(() => { });
    };

    socket.on('chat:newMessage', handler);
    return () => { socket.off('chat:newMessage', handler); };
  }, [conversation.id, myId, onNewMessage, socket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const msg = await chatApi.sendMessage(conversation.id, text.trim());
      setMessages((prev) => [...prev, msg]);
      onNewMessage(msg);
      setText('');
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async (payload: { dates: string[]; times: string[]; durationHours: string; durationMinutes: string }) => {
    setSending(true);
    try {
      const msg = await chatApi.scheduleMeeting(conversation.id, payload);
      setMessages((prev) => [...prev, msg]);
      onNewMessage(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{
        px: 2, py: 1.5, borderBottom: (t) => `1px solid ${alpha(t.palette.grey[500], 0.16)}`, flexShrink: 0
      }}>
        <IconButton size="small" onClick={onBack} edge="start">
          <Icon icon="eva:arrow-ios-back-fill" width={20} height={20} />
        </IconButton>
        <Avatar src={getAvatarSrc(interlocutor?.avatar)} sx={{ width: 32, height: 32, fontSize: 14 }} >
          {getDisplayName(interlocutor ?? {})[0]}
        </Avatar>
        <Typography variant="subtitle1" fontWeight={600}>
          {getDisplayName(interlocutor ?? {})}
        </Typography>
      </Stack>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {messages.map((msg) => (
              <Stack key={msg.id} alignItems={msg.senderId === myId ? 'flex-end' : 'flex-start'}>
                <Typography variant="caption" color="text.disabled" sx={{ mb: 0.5 }}>
                  {new Date(msg.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
                <MessageBubble isOwn={msg.senderId === myId}>
                  <Typography variant="body1">{msg.text}</Typography>
                </MessageBubble>
              </Stack>
            ))}
            <Box ref={messagesEndRef} />
          </Stack>
        )}

        {/* {!loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button variant="contained" size="small" onClick={() => setScheduleOpen(true)} sx={{ borderRadius: 1 }}>
              Назначить встречу
            </Button>
          </Box>
        )} */}
      </Box>

      {/* Input */}
      <MessageInputBar>
        <InputBase
          fullWidth
          placeholder="Напишите сообщение"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          multiline
          maxRows={4}
          sx={{ flex: 1, fontSize: 16, mr: 1 }}
        />
        <Divider orientation="vertical" flexItem sx={{ mr: 1, my: 0.5 }} />
        <IconButton size="small" color="default">
          <Icon icon="eva:attach-2-fill" width={22} height={22} />
        </IconButton>
        <IconButton size="small" color="primary" onClick={handleSend} disabled={!text.trim() || sending}>
          <Icon icon="eva:paper-plane-fill" width={22} height={22} />
        </IconButton>
      </MessageInputBar>

      <ScheduleMeetingDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} onSend={handleSchedule} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const Chat = () => {
  const { user } = personalDataStore;
  const myId = user?.id ?? '';
  const [searchParams] = useSearchParams();

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (myId) {
      chatStore.connectSocket(myId);
      chatStore.fetchUnreadCount();
    }
    return () => { chatStore.disconnectSocket(); };
  }, [myId]);

  useEffect(() => {
    chatApi.getConversations()
      .then((convs) => {
        setConversations(convs);
        const preselect = searchParams.get('conversationId');
        if (preselect) {
          setSelectedId(preselect);
        }
      })
      .finally(() => setLoading(false));
  }, [searchParams]);

  const selectedConversation = conversations.find((c) => c.id === selectedId) ?? null;

  const handleNewMessage = useCallback((msg: ChatMessage) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === msg.conversationId
          ? { ...c, messages: [msg], updatedAt: msg.createdAt }
          : c
      ).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    );
  }, []);

  const filtered = conversations.filter((c) => {
    const interlocutor = getInterlocutor(c, myId);
    if (!interlocutor) return true;
    const name = getDisplayName(interlocutor).toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <Box sx={{ height: '100%', mx: -4, mb: -4, mt: -2, display: 'flex', overflow: 'hidden' }}>
      {/* Conversation list */}
      <Box sx={{
        width: { xs: selectedConversation ? 0 : '100%', md: 380 },
        minWidth: { md: 380 },
        display: { xs: selectedConversation ? 'none' : 'flex', md: 'flex' },
        flexDirection: 'column',
        borderRight: (t) => `1px solid ${alpha(t.palette.grey[500], 0.16)}`,
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: 'background.paper'
      }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Личные сообщения
          </Typography>
          <SearchInput value={search} onChange={setSearch} placeholder="Поиск" fullWidth />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 1, pb: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: 'center' }}>
              Нет диалогов
            </Typography>
          ) : (
            filtered.map((conv) => {
              const interlocutor = getInterlocutor(conv, myId);
              const lastMsg = conv.messages[0];
              return (
                <ConversationItem key={conv.id} selected={conv.id === selectedId} onClick={() => setSelectedId(conv.id)}>
                  <Avatar
                    src={getAvatarSrc(interlocutor?.avatar)}
                    sx={{ width: 48, height: 48, flexShrink: 0 }}
                    imgProps={{ referrerPolicy: 'no-referrer', crossOrigin: "anonymous" }}
                  >
                    {getDisplayName(interlocutor ?? {})[0]}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                      <Typography variant="subtitle2" noWrap fontWeight={600}>
                        {getDisplayName(interlocutor ?? {})}
                      </Typography>
                      {lastMsg && (
                        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, ml: 1 }}>
                          {new Date(lastMsg.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </Stack>
                    <Typography variant="caption" noWrap color="primary">
                      {interlocutor?.email}
                    </Typography>
                    {lastMsg && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {lastMsg.text}
                      </Typography>
                    )}
                  </Box>
                </ConversationItem>
              );
            })
          )}
        </Box>
      </Box>

      {/* Chat panel */}
      <Box sx={{
        flex: 1,
        display: { xs: selectedConversation ? 'flex' : 'none', md: 'flex' },
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'background.paper'
      }}>
        {selectedConversation ? (
          <ChatView
            key={selectedConversation.id}
            conversation={selectedConversation}
            myId={myId}
            onBack={() => setSelectedId(null)}
            onNewMessage={handleNewMessage}
          />
        ) : (
          <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }} spacing={1}>
            <Icon icon="eva:message-circle-outline" width={48} height={48} color="#919EAB" />
            <Typography variant="body1" color="text.disabled">Выберите диалог</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default observer(Chat);
